import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  IndianRupee, TrendingUp, Receipt, AlertTriangle, Download, FileText,
  BarChart3, Building2, CreditCard, Shield, Calculator, Trash2, RotateCcw,
  Loader2, Eye, EyeOff, Link2, CheckCircle2, PlugZap,
  Activity, ShieldCheck, ShieldAlert, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

const exportCSV = (rows, filename) => {
  const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  toast.success(`${filename} downloaded!`);
};

// ---------- RazorpayX Bank Config Panel ----------
function BankConfigPanel({ bank, onSaved }) {
  const [accountNumber, setAccountNumber] = useState('');
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  // Phase 271b — Live diagnostic preflight
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, status, message }
  // Phase 272b — Simulated webhook self-test
  const [simulating, setSimulating] = useState(false);
  // Phase 272c — Pipeline activity telemetry (last 7 days)
  const [telemetry, setTelemetry] = useState(null);

  const loadTelemetry = async () => {
    try {
      const r = await api.get('/super-admin/finance/bank-config/telemetry');
      setTelemetry(r.data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    let alive = true;
    api.get('/super-admin/finance/bank-config/telemetry')
      .then(r => { if (alive) setTelemetry(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const runDiagnostic = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/super-admin/finance/bank-config/test');
      setTestResult(r.data);
      if (r.data.ok) {
        toast.success(r.data.message);
        // Phase 271c — pull the freshly stamped last_verified_at so the
        // staleness pill and footnote refresh without a page reload.
        try {
          const fresh = await api.get('/super-admin/finance/bank-config');
          onSaved?.(fresh.data);
        } catch { /* non-fatal */ }
      } else {
        toast.error(r.data.message);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Diagnostic failed';
      setTestResult({ ok: false, status: 'error', message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const runSimulatedWebhook = async () => {
    setSimulating(true);
    try {
      const r = await api.post('/super-admin/finance/bank-config/simulate-test-webhook');
      if (r.data.ok) {
        toast.success(r.data.message || '✓ Webhook pipeline validated');
        // Phase 272c — refresh the throughput counter so the count bumps in real time.
        loadTelemetry();
      } else {
        toast.error(r.data.message || 'Simulated webhook failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Simulated webhook crashed');
    } finally {
      setSimulating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10,20}$/.test(accountNumber.trim())) {
      toast.error('Corporate Account Number must be 10–20 digits');
      return;
    }
    if (keyId.trim().length < 8) {
      toast.error('RazorpayX Key ID looks too short');
      return;
    }
    if (keySecret.trim().length < 8) {
      toast.error('RazorpayX Client Secret looks too short');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/super-admin/finance/bank-config', {
        account_number: accountNumber.trim(),
        razorpayx_key_id: keyId.trim(),
        razorpayx_key_secret: keySecret.trim(),
      });
      toast.success('RazorpayX corporate bank-feed credentials saved (encrypted at rest)');
      setAccountNumber('');
      setKeyId('');
      setKeySecret('');
      setShowSecret(false);
      onSaved?.(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail
        || (Array.isArray(err.response?.data?.errors) && err.response.data.errors[0]?.message)
        || 'Failed to save bank config';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="bank-config-panel" className="border-emerald-200/70 dark:border-emerald-900/40">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center">
              <PlugZap className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-sm">Automated Bank Feed Integration (RazorpayX)</CardTitle>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Connect the Out-Play corporate current account to enable real-time credits, debits, payroll & GST reconciliation.
              </p>
            </div>
          </div>
          {bank?.configured ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge data-testid="bank-config-status-configured" className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Bank linked • ••••{bank.account_number_last4}
              </Badge>
              {bank?.is_stale && (
                <Badge
                  data-testid="bank-config-stale-pill"
                  variant="outline"
                  className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px] flex items-center gap-1"
                  title="Last successful diagnostic > 30 days ago"
                >
                  <AlertTriangle className="h-3 w-3" /> Stale Credentials — Run Diagnostic
                </Badge>
              )}
            </div>
          ) : (
            <Badge data-testid="bank-config-status-pending" variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">
              <Link2 className="h-3 w-3 mr-1" /> Awaiting Corporate Bank Link
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {bank?.configured && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Account</p>
              <p className="font-mono mt-1">••••••••••{bank.account_number_last4}</p>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Key ID</p>
              <p className="font-mono mt-1">{bank.key_id_preview || '—'}</p>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Secret</p>
              <p className="font-mono mt-1">{bank.key_secret_on_file ? '🔒 on file (encrypted)' : '—'}</p>
              {bank.updated_at && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Updated {new Date(bank.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  {bank.updated_by ? ` by ${bank.updated_by}` : ''}
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bank-acct" className="text-xs">Corporate Account Number</Label>
              <Input
                id="bank-acct"
                data-testid="bank-config-account-input"
                inputMode="numeric"
                pattern="\d*"
                maxLength={20}
                placeholder="10–20 digit current account number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank-key-id" className="text-xs">RazorpayX Key ID</Label>
              <Input
                id="bank-key-id"
                data-testid="bank-config-key-id-input"
                placeholder="rzpx_live_…"
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bank-key-secret" className="text-xs">RazorpayX Client Secret</Label>
            <div className="relative">
              <Input
                id="bank-key-secret"
                data-testid="bank-config-key-secret-input"
                type={showSecret ? 'text' : 'password'}
                placeholder="••••••••"
                value={keySecret}
                onChange={(e) => setKeySecret(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                data-testid="bank-config-toggle-secret"
                onClick={() => setShowSecret(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Secret is encrypted with the platform&apos;s Fernet key before being written to Mongo. Only a 4-char preview is ever returned to this UI.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="submit"
              data-testid="bank-config-save-btn"
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlugZap className="h-4 w-4 mr-1" />}
              {bank?.configured ? 'Rotate Credentials' : 'Connect Bank Feed'}
            </Button>
          </div>
        </form>

        {/* Phase 271b — Connection Telemetry Diagnostic */}
        <div className="mt-5 pt-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-0.5">
            <p className="text-xs font-semibold flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-slate-500" />
              Connection Telemetry Diagnostic
            </p>
            <p className="text-[11px] text-muted-foreground">
              Server-to-server handshake against <span className="font-mono">api.razorpay.com/v1/contacts</span>. Decrypts the secret at rest and validates RBAC scope before any statements are ingested.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {testResult && testResult.ok && (
              <Badge data-testid="bank-config-test-success" className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Verified
              </Badge>
            )}
            {testResult && !testResult.ok && (
              <Badge data-testid="bank-config-test-failed" variant="outline" className="border-rose-300 text-rose-700 dark:text-rose-300 text-[10px] flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" /> {testResult.status === 'auth_failed' ? 'Auth failed' : (testResult.status === 'not_configured' ? 'Not configured' : 'Failed')}
              </Badge>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="bank-config-test-btn"
              onClick={runDiagnostic}
              disabled={testing || !bank?.configured}
              title={!bank?.configured ? 'Save credentials before running the diagnostic' : undefined}
            >
              {testing
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Testing…</>
                : <><Activity className="h-3.5 w-3.5 mr-1" /> Test Connection</>}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="bank-config-simulate-webhook-btn"
              onClick={runSimulatedWebhook}
              disabled={simulating || !bank?.webhook_secret_on_file}
              title={!bank?.webhook_secret_on_file ? 'Save a webhook secret first' : 'Inject a signed test event into the live webhook handler'}
            >
              {simulating
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Injecting…</>
                : <><FlaskConical className="h-3.5 w-3.5 mr-1" /> Inject Test Webhook</>}
            </Button>
          </div>
        </div>
        {testResult?.message && (
          <p
            data-testid="bank-config-test-message"
            className={`mt-2 text-[11px] ${testResult.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
          >
            {testResult.message}
            {testResult.http_status ? <span className="font-mono ml-1">(HTTP {testResult.http_status})</span> : null}
          </p>
        )}
        {/* Phase 271c — last-verified timestamp footnote */}
        {bank?.configured && (
          <p
            data-testid="bank-config-last-verified"
            className={`mt-2 text-[10px] ${bank.is_stale ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}
          >
            {bank.last_verified_at
              ? <>Last verified: <span className="font-mono">{new Date(bank.last_verified_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>{bank.is_stale ? ' — > 30 days ago. Re-run the diagnostic.' : ''}</>
              : <>Never verified — click <span className="font-medium">Test Connection</span> to validate the saved credentials.</>}
          </p>
        )}

        {/* Phase 272c — Pipeline activity counter (last 7 days) */}
        {telemetry && (telemetry.real_events > 0 || telemetry.simulated_events > 0) && (
          <div
            data-testid="bank-config-telemetry-strip"
            className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1.5 text-[11px] rounded-lg border bg-muted/30 px-3 py-2"
          >
            <div className="flex items-center gap-1.5 font-semibold">
              <BarChart3 className="h-3.5 w-3.5 text-slate-500" />
              <span>Pipeline activity (last {telemetry.window_days}d):</span>
            </div>
            <span data-testid="telemetry-real-count" className="flex items-center gap-1">
              Real events:{' '}
              <span className="font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded font-mono">
                {telemetry.real_events}
              </span>
            </span>
            <span className="text-muted-foreground">·</span>
            <span data-testid="telemetry-simulated-count" className="flex items-center gap-1">
              Simulated:{' '}
              <span className="font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border px-1.5 py-0.5 rounded font-mono">
                {telemetry.simulated_events}
              </span>
            </span>
            {(telemetry.real?.credits_paise > 0 || telemetry.real?.debits_paise > 0) && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground" data-testid="telemetry-real-money">
                  Real flow:{' '}
                  <span className="font-mono text-emerald-700 dark:text-emerald-300">+₹{(telemetry.real.credits_paise / 100).toLocaleString('en-IN')}</span>
                  {' / '}
                  <span className="font-mono text-rose-700 dark:text-rose-300">-₹{(telemetry.real.debits_paise / 100).toLocaleString('en-IN')}</span>
                </span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Awaiting-Bank Placeholder ----------
function AwaitingBankPlaceholder({ tabLabel }) {
  return (
    <Card data-testid={`awaiting-bank-${tabLabel.toLowerCase()}`} className="border-dashed">
      <CardContent className="py-12 flex flex-col items-center text-center gap-3">
        <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center">
          <Link2 className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-medium">{tabLabel} are waiting for a live RazorpayX connection</p>
          <p className="text-xs text-muted-foreground max-w-md mt-1">
            Once the corporate current account is linked from the <span className="font-medium">Accounts</span> tab,
            authenticated banking streams will populate this view in real time. Until then, no fabricated numbers are shown.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">
          Awaiting Corporate Bank Link
        </Badge>
      </CardContent>
    </Card>
  );
}

export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 257b — Deleted fees audit feed
  const [deletedFees, setDeletedFees] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  // Phase 271 — RazorpayX bank-feed config
  const [bank, setBank] = useState(null);

  const loadDeleted = () => {
    api.get('/super-admin/finance/deleted-fees?days=30')
      .then(r => setDeletedFees(r.data))
      .catch(() => setDeletedFees({ count: 0, total_amount: 0, items: [] }));
  };

  const loadBank = () => {
    api.get('/super-admin/finance/bank-config')
      .then(r => setBank(r.data))
      .catch(() => setBank({ configured: false }));
  };

  useEffect(() => {
    api.get('/super-admin/finance').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    loadDeleted();
    loadBank();
  }, []);

  const handleRestore = async (fee) => {
    setRestoringId(fee.id);
    try {
      await api.post(`/super-admin/finance/deleted-fees/${fee.id}/restore`);
      toast.success(`Restored ${fee.custom_id || fee.id.slice(0, 8)} (₹${Number(fee.amount).toLocaleString()})`);
      loadDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24 rounded-xl" />)}</div>
    </div>
  );

  const d = data || {};
  const bankReady = !!bank?.configured;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold">Finance</h1>
        <p className="text-sm text-muted-foreground">
          Out-Play corporate revenue, subscription KPIs & bank-feed reconciliation
        </p>
      </div>

      {/* Honest disclosure banner when bank is not yet connected */}
      {!bankReady && (
        <Card data-testid="bank-disclosure-banner" className="border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900/40">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 dark:text-amber-200">
              <p className="font-medium">Corporate bank feed not yet linked.</p>
              <p className="mt-0.5 text-amber-800/80 dark:text-amber-300/80">
                The KPI cards and Dashboard tab below show live SaaS subscription & fee revenue derived from your own database.
                Accounts, Transactions, Reports and Taxes will activate once RazorpayX is connected from the Accounts tab — no placeholder numbers are shown.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards (always live from /super-admin/finance) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card data-testid="kpi-total-revenue" className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 dark:from-emerald-950/30 dark:to-teal-950/30 dark:border-emerald-900/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Total Revenue</span><TrendingUp className="h-4 w-4 text-emerald-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-emerald-700 dark:text-emerald-400">₹{((d.total_revenue||0)/1000).toFixed(1)}K</p>
            <p className="text-[10px] text-emerald-600">All academies</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-mrr" className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 dark:from-blue-950/30 dark:to-indigo-950/30 dark:border-blue-900/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">MRR</span><IndianRupee className="h-4 w-4 text-blue-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-blue-700 dark:text-blue-400">₹{((d.mrr||0)/1000).toFixed(1)}K</p>
            <p className="text-[10px] text-blue-600">Monthly recurring</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-arr" className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200 dark:from-purple-950/30 dark:to-violet-950/30 dark:border-purple-900/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">ARR (Projected)</span><TrendingUp className="h-4 w-4 text-purple-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-purple-700 dark:text-purple-400">₹{((d.arr||0)/1000).toFixed(1)}K</p>
            <p className="text-[10px] text-purple-600">Subscription × 12</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-pending" className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-900/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Pending</span><AlertTriangle className="h-4 w-4 text-amber-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-amber-700 dark:text-amber-400">₹{((d.pending_invoices?.amount||0)/1000).toFixed(1)}K</p>
            <p className="text-[10px] text-amber-600">{d.pending_invoices?.count||0} invoices</p>
          </CardContent>
        </Card>
      </div>

      {/* 6-Tab Finance System (state-driven by `bankReady`) */}
      <Tabs defaultValue="dashboard">
        <TabsList className="w-full flex">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="flex-1 text-xs"><BarChart3 className="h-3.5 w-3.5 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts" className="flex-1 text-xs"><Building2 className="h-3.5 w-3.5 mr-1" /> Accounts</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions" className="flex-1 text-xs"><CreditCard className="h-3.5 w-3.5 mr-1" /> Transactions</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className="flex-1 text-xs"><FileText className="h-3.5 w-3.5 mr-1" /> Reports</TabsTrigger>
          <TabsTrigger value="taxes" data-testid="tab-taxes" className="flex-1 text-xs"><Calculator className="h-3.5 w-3.5 mr-1" /> Taxes</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" className="flex-1 text-xs"><Shield className="h-3.5 w-3.5 mr-1" /> Audit</TabsTrigger>
        </TabsList>

        {/* Dashboard — live SaaS data */}
        <TabsContent value="dashboard">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Revenue Breakdown</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Subscription Revenue</span><span className="font-semibold tabular-nums" data-testid="rev-subscription">₹{(d.subscription_revenue||0).toLocaleString()}</span></div>
              <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Fee Revenue (Academies)</span><span className="font-semibold tabular-nums" data-testid="rev-fees">₹{(d.fee_revenue||0).toLocaleString()}</span></div>
              <Separator />
              <div className="flex items-center justify-between"><span className="text-sm font-medium">Total</span><span className="font-bold tabular-nums text-emerald-600" data-testid="rev-total">₹{(d.total_revenue||0).toLocaleString()}</span></div>
            </CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Top Academies</CardTitle></CardHeader><CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>Academy</TableHead><TableHead>Revenue</TableHead><TableHead>Students</TableHead></TableRow></TableHeader>
              <TableBody>
                {(d.top_academies||[]).map((a,i) => (
                  <TableRow key={i}>
                    <TableCell><div><p className="font-medium text-sm">{a.academy_name}</p><p className="text-xs font-mono text-muted-foreground">{a.academy_custom_id}</p></div></TableCell>
                    <TableCell className="tabular-nums font-semibold">₹{a.revenue.toLocaleString()}</TableCell>
                    <TableCell className="tabular-nums">{a.students}</TableCell>
                  </TableRow>
                ))}
                {(d.top_academies||[]).length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No data</TableCell></TableRow>}
              </TableBody>
            </Table></CardContent></Card>
          </div>
        </TabsContent>

        {/* Accounts — RazorpayX bank-feed config + (when linked) live wired view */}
        <TabsContent value="accounts" className="space-y-4">
          <BankConfigPanel bank={bank} onSaved={(b) => setBank({ configured: true, ...b })} />

          {bankReady ? (
            <Card data-testid="accounts-live-view">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Linked Accounts</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">RazorpayX Corporate Account</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">Bank</Badge></TableCell>
                      <TableCell className="font-mono text-xs">••••••••••{bank.account_number_last4}</TableCell>
                      <TableCell><Badge className="text-xs bg-emerald-600 hover:bg-emerald-600 text-white">Linked</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Razorpay PG (subscriptions & fees)</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">Payment Gateway</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">Tenant-isolated</TableCell>
                      <TableCell><Badge className="text-xs bg-emerald-600 hover:bg-emerald-600 text-white">Live</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="text-[10px] text-muted-foreground p-3">
                  Live RazorpayX statement ingestion is queued for the next phase. Balances will appear here once the corporate banking webhook is wired.
                </p>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        {/* Transactions — strictly state-driven */}
        <TabsContent value="transactions">
          {bankReady ? (
            <Card data-testid="transactions-empty-live">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Corporate Account Statement</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Debit ₹</TableHead><TableHead className="text-right">Credit ₹</TableHead></TableRow></TableHeader>
                  <TableBody>
                    <TableRow><TableCell colSpan={4} className="text-center py-10 text-xs text-muted-foreground">
                      RazorpayX is connected, but no statement entries have been ingested yet. They&apos;ll appear here in real time once the banking webhook is wired.
                    </TableCell></TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <AwaitingBankPlaceholder tabLabel="Transactions" />
          )}
        </TabsContent>

        {/* Reports — strictly state-driven */}
        <TabsContent value="reports">
          {bankReady ? (
            <Card data-testid="reports-live">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Corporate Financial Reports</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  P&amp;L, Balance Sheet and Cash Flow exports will appear here once the corporate banking stream has at least one full reconciliation cycle.
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button onClick={() => {
                    const rows = [
                      ['Revenue by Academy'],
                      [''],
                      ['Academy', 'Revenue', 'Students'],
                      ...((d.top_academies || []).map(a => [a.academy_name, '₹' + a.revenue, a.students])),
                    ];
                    exportCSV(rows, `Revenue_by_Academy_${new Date().toISOString().slice(0,10)}.csv`);
                  }} className="flex items-center gap-3 p-4 rounded-xl border hover:bg-accent/50 hover:shadow-md transition-all duration-150 text-left">
                    <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center"><Building2 className="h-5 w-5 text-amber-600" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium">Revenue by Academy</p><p className="text-[10px] text-muted-foreground">Live SaaS data</p></div>
                    <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                  <button onClick={() => {
                    const rows = [
                      ['Subscription MRR / ARR'], [''],
                      ['Metric', 'Value'],
                      ['MRR', '₹' + (d.mrr || 0)],
                      ['ARR', '₹' + (d.arr || 0)],
                      ['Total Academies', d.total_academies || 0],
                    ];
                    exportCSV(rows, `MRR_Report_${new Date().toISOString().slice(0,10)}.csv`);
                  }} className="flex items-center gap-3 p-4 rounded-xl border hover:bg-accent/50 hover:shadow-md transition-all duration-150 text-left">
                    <div className="h-10 w-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-teal-600" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium">Subscription MRR</p><p className="text-[10px] text-muted-foreground">Live SaaS data</p></div>
                    <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                  <button onClick={() => {
                    const rows = [
                      ['Pending Collections'], [''],
                      ['Count', d.pending_invoices?.count || 0],
                      ['Amount', '₹' + (d.pending_invoices?.amount || 0)],
                    ];
                    exportCSV(rows, `Pending_Collections_${new Date().toISOString().slice(0,10)}.csv`);
                  }} className="flex items-center gap-3 p-4 rounded-xl border hover:bg-accent/50 hover:shadow-md transition-all duration-150 text-left">
                    <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium">Pending Collections</p><p className="text-[10px] text-muted-foreground">Live SaaS data</p></div>
                    <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <AwaitingBankPlaceholder tabLabel="Reports" />
          )}
        </TabsContent>

        {/* Taxes — strictly state-driven */}
        <TabsContent value="taxes">
          {bankReady ? (
            <Card data-testid="taxes-live">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Statutory Filings</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  GSTR-1, GSTR-3B, 26AS and ITC reconciliation will surface here once the corporate banking stream feeds real debits/credits with HSN/SAC tags.
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"><Receipt className="h-5 w-5 text-emerald-600" /></div>
                      <div><p className="text-sm font-semibold">GSTR-1 / 3B</p><p className="text-[10px] text-muted-foreground">Awaiting first bank cycle</p></div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">Pending live data</Badge>
                  </CardContent></Card>
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"><FileText className="h-5 w-5 text-blue-600" /></div>
                      <div><p className="text-sm font-semibold">TDS 26AS</p><p className="text-[10px] text-muted-foreground">Awaiting first bank cycle</p></div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">Pending live data</Badge>
                  </CardContent></Card>
                </div>
              </CardContent>
            </Card>
          ) : (
            <AwaitingBankPlaceholder tabLabel="Taxes" />
          )}
        </TabsContent>

        {/* Audit — always shows real Deleted Fees ledger (live data) */}
        <TabsContent value="audit" className="space-y-4">
          <Card data-testid="deleted-fees-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-lg bg-rose-100 dark:bg-rose-950/40 flex items-center justify-center">
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </div>
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2">
                      Deleted Fees
                      <Badge variant="outline" data-testid="deleted-fees-count" className="text-[10px]">
                        {deletedFees?.count ?? '…'} in last 30 days
                      </Badge>
                    </CardTitle>
                    <p className="text-[10px] text-muted-foreground">
                      Total purged value: <span className="font-mono">₹{Number(deletedFees?.total_amount || 0).toLocaleString()}</span>. Restore window = 30 days.
                    </p>
                  </div>
                </div>
                {deletedFees?.items?.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const rows = [
                        ['Date', 'Academy', 'Student', 'Invoice', 'Amount (₹)', 'Status When Deleted', 'Deleted By'],
                        ...deletedFees.items.map(i => [
                          new Date(i.deleted_at).toLocaleString('en-IN'),
                          i.academy_name, i.student_name, i.custom_id || i.id.slice(0, 8),
                          i.amount, i.status_when_deleted, i.deleted_by_email,
                        ]),
                      ];
                      exportCSV(rows, `Deleted_Fees_${new Date().toISOString().slice(0,10)}.csv`);
                    }}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {deletedFees == null ? (
                <div className="p-6"><Skeleton className="h-16 w-full" /></div>
              ) : deletedFees.items.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground" data-testid="deleted-fees-empty">
                  No fees have been deleted in the last 30 days. Clean ledger.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Deleted At</TableHead>
                      <TableHead>Academy</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Deleted By</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedFees.items.map(fee => (
                      <TableRow key={fee.id} data-testid={`deleted-fee-row-${fee.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{new Date(fee.deleted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</TableCell>
                        <TableCell className="text-sm">{fee.academy_name}</TableCell>
                        <TableCell className="text-sm">{fee.student_name}</TableCell>
                        <TableCell className="font-mono text-[11px]">{fee.custom_id || fee.id.slice(0, 8)}</TableCell>
                        <TableCell className="text-right font-mono">₹{Number(fee.amount).toLocaleString()}</TableCell>
                        <TableCell className="text-[11px] text-muted-foreground">{fee.deleted_by_email || '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestore(fee)}
                            disabled={restoringId === fee.id}
                            data-testid={`restore-fee-${fee.id}`}
                            className="h-7 text-xs"
                          >
                            {restoringId === fee.id
                              ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              : <RotateCcw className="h-3 w-3 mr-1" />}
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
